import { Tray } from "@phosphor-icons/react";
import { forwardRef } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import type { EmptyStateProps, EmptyStateVariant } from "./EmptyState.types";

interface EmptyStateOwnerState {
  variant: EmptyStateVariant;
}

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const dashedPanel: CSSObject = {
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  borderStyle: "dashed",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
};

const emptyStateVariantStyles: Record<EmptyStateVariant, CSSObject> = {
  default: {
    minHeight: "14rem",
    ...dashedPanel,
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    paddingTop: "2.5rem",
    paddingBottom: "2.5rem"
  },
  compact: {
    ...dashedPanel,
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "1.25rem",
    paddingBottom: "1.25rem"
  },
  "full-page": {
    minHeight: "320px",
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    paddingTop: "3rem",
    paddingBottom: "3rem"
  },
  table: {
    minHeight: "11rem",
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    paddingTop: "2rem",
    paddingBottom: "2rem"
  }
};

const EmptyStateRoot = styled("div", {
  label: "AgenticEmptyState-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: EmptyStateOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center"
  },
  emptyStateVariantStyles[ownerState.variant]
]);

const EmptyStateIconBubble = styled("div", {
  label: "AgenticEmptyState-iconBubble",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: EmptyStateOwnerState }>(({ ownerState }) => [
  {
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-pill)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.variant === "compact"
    ? { width: "2.5rem", height: "2.5rem" }
    : { width: "3rem", height: "3rem" }
]);

const EmptyStateTitle = styled("h3", {
  label: "AgenticEmptyState-title",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: EmptyStateOwnerState }>(({ ownerState }) => [
  {
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.variant === "compact"
    ? { fontSize: ".875rem", lineHeight: "1.25rem" }
    : { fontSize: "1rem", lineHeight: "1.5rem" }
]);

const EmptyStateDescription = styled("p", {
  label: "AgenticEmptyState-description"
})({
  marginTop: ".5rem",
  maxWidth: "28rem",
  fontSize: ".875rem",
  lineHeight: "1.5rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const EmptyStateActions = styled("div", {
  label: "AgenticEmptyState-actions"
})({
  marginTop: "1.25rem",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: ".5rem"
});

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ className, icon, illustration, title, description, primaryAction, secondaryAction, action, variant = "default", ...props }, ref) => {
    const ownerState: EmptyStateOwnerState = { variant };
    return (
      <EmptyStateRoot ref={ref} ownerState={ownerState} className={className} {...props}>
        {illustration ?? (
          <EmptyStateIconBubble ownerState={ownerState}>
            {icon ?? <Tray size={variant === "compact" ? 20 : 24} weight="duotone" />}
          </EmptyStateIconBubble>
        )}
        <EmptyStateTitle ownerState={ownerState}>{title}</EmptyStateTitle>
        {description && <EmptyStateDescription>{description}</EmptyStateDescription>}
        {(primaryAction || secondaryAction || action) && (
          <EmptyStateActions>
            {action}
            {secondaryAction}
            {primaryAction}
          </EmptyStateActions>
        )}
      </EmptyStateRoot>
    );
  }
);

EmptyState.displayName = "EmptyState";
